import React from 'react'
import Helmet from 'react-helmet'
import { graphql } from 'gatsby'
import get from 'lodash/get'
import Img from 'gatsby-image'
import {
  Breadcrumbs,
  Card,
  Divider,
  Hero,
  Icon,
  Link,
  Tag,
  Tile
} from '@storaensods/seeds-react'
import Layout from '../components/layout'
import Difficulty from '../components/difficulty'

class DesignToolTemplate extends React.Component {
  render() {
    const tool = get(this.props, 'data.contentfulServiceDesignTool')

    return (
      <Layout location={this.props.location}>
        <Helmet title={`${tool.name} | Service Design Toolkit | Stora Enso Design System`} >
        <script async src={`https://www.googletagmanager.com/gtag/js?id=UA-157026851-1`} />
        <script>
        {`
           window.dataLayer = window.dataLayer || [];
           function gtag(){dataLayer.push(arguments);}
           gtag('js', new Date());
           gtag('config', 'UA-157026851-1');
        `}
        </script>  
        </Helmet>
        <article>

        <div className="se-hero se-hero--md" style={{"backgroundImage":"url("+tool.image.resolutions.src+")"}}>
          <div className="se-hero-content-container container">
          <div className="se-hero-content">
            <div className="se-tile se-tile--yellow">
              <h1>
                {tool.name}
              </h1>
              <div
                dangerouslySetInnerHTML={{
                  __html: tool.shortDescription.childMarkdownRemark.html,
                }}
              />
            </div>
          </div>
          </div>
        </div>

         
        <div className="container">
          <div style={{ marginTop: '-2rem' }}>
            <Breadcrumbs>
              <Link href="/">
                SEEDS Home
              </Link>
              <Link href="/toolkit">
                Playbook
              </Link>
              <Link href="/toolkit?tab=toolkit">
                Toolkit
              </Link>
              <Link>
                {tool.name}
              </Link>
            </Breadcrumbs>
          </div>
          <section className="row">
            {tool.description && (
              <div
                className="col"
                dangerouslySetInnerHTML={{
                  __html: tool.description.childMarkdownRemark.html,
                }}
              />
            )}
            <div className="col-md-4">
              <Tile color="light">
                <div>
                  <strong>People</strong>
                  <p>{tool.people}</p>
                </div>

                <div>
                  <strong>Time</strong>
                  <p>{tool.time}</p>
                </div>

                <div>
                  <strong>Difficulty</strong>
                  <Difficulty difficulty={tool.difficulty} />
                </div>

                <div>
                  <strong>Materials</strong>
                  <ul>
                    {tool.materials && tool.materials.map((material) => (
                      <li key={material}>{material}</li>
                    ))}
                  </ul>
                </div>

                <div className="se-mt-lg">
                  <strong>Download Resources</strong>
                  <ul className="se-mt-xs">
                    {tool.resources && tool.resources.map((resource) => (
                      <li className="se-mb-xs" key={resource.title}>
                        <Link href={resource.file.url}>
                          <Icon color="yellow">
                            file_download
                          </Icon>
                          <span>Download {resource.title}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </Tile>
            </div>
          </section>
          <Divider />
          <section>
            <header>
              <h2 className="se-mb-xl">Instructions</h2>
            </header>
            {tool.steps && tool.steps.map(step => (
              <div key={step.title} className="row">
                <Tile color="light" className="col se-mb-lg">
                  <h3 className="se-h5 se-section-title se-mb-lg">{step.title}</h3>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: step.description.childMarkdownRemark.html,
                    }}
                  />
                </Tile>
                <div className="col-md-4">
                  {step.tip && (
                    <div className="se-mb-xl">
                      <Tile color="yellow">
                        <strong>Tips for {step.title}</strong>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: step.tip.childMarkdownRemark.html,
                          }}
                        />
                      </Tile>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </section>
          { tool.case_study && (
            <>
              <Divider />
              <section className="se-mb-2xl">
                <h2 className="se-mb-md">Related case studies</h2>
                <div className="row">
                  { tool.case_study.map(study => (
                    <div key={study.title} className="col-md-6 se-mb-md">
                      <Card title={study.title}>
                        <Img alt="" sizes={study.image.sizes} style={{ width: '100%' }} className="se-mb-md" />
                        <div
                          dangerouslySetInnerHTML={{
                            __html: study.shortDescription.childMarkdownRemark.html,
                          }}
                        />
                        <div className="se-mb-md">
                          { study.tools.map(study_tool => (
                            <Tag key={study_tool.name} className="se-mr-xs">{study_tool.name}</Tag>
                          ))}
                        </div>
                        <Link href={`/case-studies/${study.slug}`}>
                          <Icon color="yellow">
                            chevron_right
                          </Icon>
                          <span>Go to {study.title}</span>
                        </Link>
                      </Card>
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}
          {tool && tool.links && (
            <div>
              <Divider />
              <section className="se-mb-2xl">
                <div>
                  {tool.links.map(link => (
                    <a
                    key={link.title}
                      className="se-btn se-btn--blue se-fullwidth"
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link.title}
                    </a>
                  ))}
                </div>
              </section>
            </div>
          )}
          </div>
        </article>
      </Layout>
    )
  }
}

export default DesignToolTemplate

export const pageQuery = graphql`
  query ServiceDesignToolBySlug($slug: String!) {
    contentfulServiceDesignTool(slug: { eq: $slug }) {
      name
      image {
        resolutions(width: 1280) {
          src
        }
      }
      shortDescription {
        childMarkdownRemark {
          html
        }
      }
      description {
        childMarkdownRemark {
          html
        }
      }
      people
      time
      difficulty
      materials
      resources {
        title
        file { url }
      }
      steps {
        ...on ContentfulServiceDesignToolStep {
          title
        }
        description {
          childMarkdownRemark {
            html
          }
        }
        tip {
          childMarkdownRemark {
            html
          }
        }
      }
      case_study {
        title
        slug
        image {
          sizes(maxWidth: 500, maxHeight: 250, resizingBehavior: THUMB) {
           ...GatsbyContentfulSizes_withWebp
          }
        }
        shortDescription {
          childMarkdownRemark {
            html
          }
        }
        tools {
          name
        }
      }
      links {
        ...on ContentfulLinkButton {
          title
          url
        }
      }
    }
  }
`
